// src/components/OeuvresEnVente.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Helper d’image robuste + lazy loading
function getImageWithFallback(artwork, fallback = "/placeholder.jpg") {
  if (!artwork) return fallback;

  if (typeof artwork.image_url === "string" && artwork.image_url.trim() !== "") {
    return artwork.image_url;
  }

  if (artwork.images) {
    try {
      const imgs =
        typeof artwork.images === "string"
          ? JSON.parse(artwork.images)
          : Array.isArray(artwork.images)
            ? artwork.images
            : [];

      if (Array.isArray(imgs) && imgs.length > 0) {
        const first = imgs.find((image) => image?.isPrimary) || imgs[0];
        if (typeof first === "object" && first !== null) {
          return first.url || first.path || fallback;
        }
        return first;
      }
    } catch (error) {
      console.warn("[OeuvresEnVente] image parsing error:", error);
    }
  }

  const possibleFields = ["image", "img_url", "thumbnail_url", "photo_url"];
  for (const key of possibleFields) {
    if (typeof artwork[key] === "string" && artwork[key].trim() !== "") {
      return artwork[key];
    }
  }

  return fallback;
}

export default function OeuvresEnVente({ onUpdate }) {
  const { t, i18n } = useTranslation();
  const [artworks, setArtworks] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const locale = useMemo(() => {
    const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
  }, [i18n.language, i18n.resolvedLanguage]);

  const formatAmount = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") {
        return t("artworksForSale.undefined");
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return String(value);

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numericValue);
    },
    [locale, t]
  );

  useEffect(() => {
    let mounted = true;

    const fetchArtworks = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.warn("[OeuvresEnVente] Supabase session error:", sessionError.message);
        }

        const currentUserId = sessionData?.session?.user?.id || null;
        if (mounted) setUserId(currentUserId);

        const { data: artworksData, error: artworksError } = await supabase
          .from("artworks")
          .select("*")
          .eq("is_public_for_sale", true);

        if (artworksError) throw artworksError;
        if (mounted) setArtworks(Array.isArray(artworksData) ? artworksData : []);
      } catch (err) {
        console.error("[OeuvresEnVente] artwork loading error:", err);
        if (mounted) {
          setError(err?.message || t("artworksForSale.errors.loadingUnknown"));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchArtworks();
    return () => {
      mounted = false;
    };
  }, [onUpdate, t]);

  const removeFromSale = async (artworkId) => {
    if (!window.confirm(t("artworksForSale.confirmRemove"))) return;

    try {
      const { error: updateError } = await supabase
        .from("artworks")
        .update({ is_public_for_sale: false })
        .eq("id", artworkId);

      if (updateError) throw updateError;

      setArtworks((current) => current.filter((artwork) => artwork.id !== artworkId));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("[OeuvresEnVente] remove from sale error:", err);
      window.alert(
        t("artworksForSale.errors.remove", {
          message: err?.message || t("artworksForSale.errors.unknown"),
        })
      );
    }
  };

  const userArtworks = artworks.filter(
    (artwork) => String(artwork.client_id) === String(userId)
  );
  const otherArtworks = artworks.filter(
    (artwork) => String(artwork.client_id) !== String(userId)
  );

  const ArtworkCard = ({ artwork, ownedByUser }) => {
    const title = artwork.title || t("artworksForSale.untitledArtwork");
    const salePrice = artwork.sale_price ?? artwork.selling_price;

    return (
      <div className="border p-4 rounded shadow-sm hover:shadow-md transition-shadow bg-white">
        <img
          loading="lazy"
          src={getImageWithFallback(artwork, "/placeholder.jpg")}
          alt={title}
          className="w-full h-60 object-cover mb-4 rounded"
          onError={(event) => {
            event.currentTarget.src = "/placeholder.jpg";
          }}
        />

        <div className="mb-2">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          {artwork.artist && (
            <p className="text-xs italic text-gray-500 mt-1">{artwork.artist}</p>
          )}
        </div>

        <p className="text-sm text-gray-600">
          {artwork.year ? `${artwork.year} — ` : ""}
          {artwork.medium || artwork.technique || t("artworksForSale.unknownTechnique")}
        </p>

        {ownedByUser ? (
          <>
            <p className="text-sm">
              {t("artworksForSale.labels.estimate")}: {formatAmount(artwork.value ?? artwork.estimated_price ?? 0)}
            </p>
            <p className="text-sm">
              {t("artworksForSale.labels.purchasePrice")}: {formatAmount(artwork.price ?? artwork.purchase_price ?? 0)}
            </p>
            <p className="text-sm mt-2 text-green-600 font-medium">
              {t("artworksForSale.labels.salePrice")}: {formatAmount(salePrice)}
            </p>
          </>
        ) : (
          <p className="text-sm font-medium">
            {t("artworksForSale.labels.proposedPrice")}: {formatAmount(salePrice)}
          </p>
        )}

        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <Link
            to={`/oeuvresenvente/${artwork.id}`}
            className="flex-1 text-center bg-gray-900 text-white py-2 rounded hover:bg-black transition-colors"
          >
            {t("artworksForSale.actions.view")}
          </Link>

          {ownedByUser ? (
            <button
              type="button"
              onClick={() => removeFromSale(artwork.id)}
              className="flex-1 text-center text-red-600 hover:underline"
            >
              {t("artworksForSale.actions.remove")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                window.location.href = `/proposal/${artwork.id}`;
              }}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-sm py-2 rounded transition-colors"
            >
              {t("artworksForSale.actions.makeOffer")}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="my-8 flex justify-center items-center text-gray-600">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2" />
        {t("artworksForSale.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-8 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded">
        {error}
      </div>
    );
  }

  if (artworks.length === 0) {
    return (
      <div className="my-8 text-center text-gray-500">
        {t("artworksForSale.empty")}
      </div>
    );
  }

  return (
    <div className="my-8 space-y-12">
      {userArtworks.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-4">
            {t("artworksForSale.sections.mine")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {userArtworks.map((artwork) => (
              <ArtworkCard key={artwork.id} artwork={artwork} ownedByUser />
            ))}
          </div>
        </section>
      )}

      {otherArtworks.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-4">
            {t("artworksForSale.sections.others")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherArtworks.map((artwork) => (
              <ArtworkCard key={artwork.id} artwork={artwork} ownedByUser={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
